

import type { UserRole, Permission, RolesConfig, RoleConfig } from '@/config/roles';

// User Preferences Types - Refactored for new table structure

// Preferences for UI theme and session settings
export interface GeneralUiPreferences {
  theme: 'light' | 'dark' | 'system';
  session: {
    idleTimeoutEnabled: boolean;
    idleTimeoutMinutes: number;
  };
}

// Preferences for dashboard summary and shortcut cards
export interface AtalhosPreferences {
  summary: {
    cards: DashboardCardSetting[];
    valuesVisible: boolean;
  };
  shortcuts: {
    cards: DashboardCardSetting[];
  };
}

// Preferences for dashboard analytics graphs
export interface GraficosPreferences {
  analytics: {
    charts: DashboardCardSetting[];
  };
}

// Combined preferences object used in the client-side code
export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  session: {
    idleTimeoutEnabled: boolean;
    idleTimeoutMinutes: number;
  };
  dashboard: {
    summary: {
      cards: DashboardCardSetting[];
      valuesVisible: boolean;
    };
    shortcuts: {
      cards: DashboardCardSetting[];
    };
    analytics: {
      charts: DashboardCardSetting[];
    };
  };
}


export interface DashboardCardSetting {
  id: string;
  visible: boolean;
}


// --- Other types remain the same ---

export interface Filament {
  id: string;
  tipoId: string; // Foreign key to tipos_filamentos
  cor: string;
  densidade: number; // g/cm³, usado para calcular peso a partir do volume se necessário
  marcaId?: string; // ID da marca
  modelo?: string; // Ex: PLA+, Standard
  temperaturaBicoIdeal?: number; // em °C
  temperaturaMesaIdeal?: number; // em °C
  precoPorKg?: number | null; // R$/kg - Essencial para o cálculo de custo do material
  quantidadeEstoqueGramas?: number; // Nova propriedade para quantidade em estoque
  estoque_minimo_gramas?: number;
  pesoRoloGramas?: number | null; // Peso do CARRETEL VAZIO em gramas (a tara do rolo), não o do filamento
  ativo: boolean;
  tipo: string; // From join
  marca: string; // From join
  ultimaConferenciaEm?: Date | string | null;
  /** `#RRGGBB` da cor real, quando conhecida. Casa o SKU com o hex do gcode. */
  corHex?: string | null;
}

// Novo tipo para o relatório de estoque
export interface FilamentStockData extends Filament {
  marcaNome: string;
  tipoNome: string;
}


export interface Printer {
  id: string;
  marcaId: string; // ID da marca
  modelo?: string;
  custoAquisicao: number;
  taxaDepreciacaoHora: number; // R$/hora - Custo por hora da impressora (depreciação)
  vidaUtilAnos: number;
  horasTrabalhoDia: number;
  ativo: boolean;
  funcionaFimSemana: boolean;
}

export interface ProductCostBreakdown {
  custoMaterialCalculado: number;
  custoImpressaoCalculado: number;
  custoTotalProducaoCalculado: number;
  lucroCalculado: number;
  precoVendaCalculado: number;
  markupCalculado?: number; // Markup percentual sobre o custo de produção
}

export interface ProdutoFilamento {
  id?: string;
  produto_id: string;
  filamento_id: string;
  peso_gramas: number;
  /**
   * Coluna do gcode que esta linha representa, 1-based.
   *
   * A coluna do fatiador NÃO é a posição física na caixa: o mesmo arquivo
   * impresso em duas máquinas usa posições diferentes. Guardar a coluna é
   * o que permite deduzir a posição na hora do envio, em vez de perguntar.
   */
  slot_gcode?: number | null;
  /** A que mesa do produto esta linha pertence. NULL em cadastro à mão. */
  placa_indice?: number | null;
}

export interface Nicho {
  id: string;
  nome: string;
  user_id: string;
  created_at?: string;
  ativo: boolean;
}

export interface GrupoItem {
  id: string;
  nome: string;
  user_id: string;
  created_at?: string;
  ativo: boolean;
}

export interface ComissaoParceiroGrupo {
  id: string;
  user_id: string;
  parceiro_id: string;
  grupo_item_id: string;
  percentual_comissao: number;
  created_at?: string;
  updated_at?: string;
}

export interface Dimensoes {
  largura?: number;
  profundidade?: number;
  altura?: number;
}

export interface VolumeDiscountTier {
  minQty: number;
  discountPct: number;
  label?: string;
}

export interface Product {
  id: string;
  nome: string;
  descricao?: string;
  imageUrls?: string[];
  imageStoragePaths?: string[];
  impressoraId: string;
  tempoImpressaoHoras: number;
  tempoImpressaoMinutos?: number; // Campo opcional para UI
  custoModelagem?: number;
  custosExtras?: number;
  margemLucroPercentual: number;
  custoDetalhado?: ProductCostBreakdown;
  custoTotalCalculado?: number;
  precoVendaCalculado?: number;
  pesoTotalGramas?: number;
  filamentos: ProdutoFilamento[];
  nichoId?: string | null;
  linkExterno?: string | null;
  nichoNome?: string; // Campo opcional para exibição
  grupoItemId?: string | null;
  grupoItemNome?: string;
  destaque?: boolean;
  ativo?: boolean;
  ecommerce?: boolean;
  mostrarPrecoEcommerce?: boolean;
  mostrar_preco_ecommerce?: boolean;
  dimensoes?: Dimensoes;
  faz_estoque?: boolean;
  regrasAtacado?: VolumeDiscountTier[];
  /**
   * Unidades que saem quando todas as placas são impressas uma vez.
   *
   * 20 para uma mesa com 20 cópias do mesmo chaveiro; 1 para um produto
   * cujas mesas são partes de uma peça só.
   */
  rendimentoPorCiclo?: number;

  /**
   * Nota livre sobre a montagem da impressão.
   *
   * Interna: não vai para orçamento, PDF nem vitrine. Serve para o que os
   * números derivados não contam -- por exemplo, que 3,22 g e 24 min são
   * um onze avos de uma mesa de 11 peças.
   */
  observacoesImpressao?: string;
  /** NCM na nota fiscal, só dígitos. Vazio: vale o NCM padrão da empresa. */
  ncm?: string | null;
  created_at?: string;
  createdAt?: string;

  /**
   * `kit`: montado a partir de outros produtos (`componentes`). Custo,
   * preço, peso e tempo do kit são calculados pelo banco a partir dos itens.
   */
  tipo?: TipoDeProduto;
  /** Kit: ajuste sobre a soma dos preços dos itens, em % ou R$. Negativo é desconto. */
  kitAjusteTipo?: 'percentual' | 'valor';
  kitAjusteValor?: number;
  componentes?: ComponenteDoKit[];
}

export type TipoDeProduto = 'produto' | 'kit';

/** Um item do kit: o produto e quantas unidades dele vão em um kit. */
export interface ComponenteDoKit {
  produtoId: string;
  nome: string;
  quantidade: number;
  custoUnitario: number;
  precoUnitario: number;
  pesoGramas: number;
  imagemUrl?: string;
}

export interface PaginatedProductsResponse {
  products: Product[];
  totalCount: number;
}

export interface Brand {
  id: string;
  nome_marca: string;
  created_at?: string;
  created_by_user_id?: string;
  is_supplier?: boolean;
  is_brand?: boolean;
  last_invoice_number?: string;
  last_purchase_date?: string;
  ativo: boolean;
}

export interface FilamentType {
  id: string;
  tipo: string;
  created_at?: string;
  ativo: boolean;
}

export interface PowerOverride {
  id: string;
  printerId: string;
  printerName: string;
  filamentTypeId: string;
  filamentTypeName: string;
  powerWatts: number;
}

/**
 * A potência específica como ela sai do banco.
 *
 * Sem os nomes de propósito: a consulta não junta impressoras nem tipos
 * de filamento. Quem precisa exibi-los resolve pelos ids contra as listas
 * que já tem em mãos -- é o que a tela de configurações faz.
 *
 * Existe como tipo com nome porque a maior parte do sistema só quer
 * `powerWatts` para uma combinação de impressora e filamento, e pedir
 * `PowerOverride` nesses lugares é prometer dois campos que nunca chegam.
 */
export type PowerOverrideRegistro = Omit<PowerOverride, 'printerName' | 'filamentTypeName'>;

export type SortableOverrideField = 'printerName' | 'filamentTypeName' | 'powerWatts';

export const OrcamentoStatusOptions = ["Solicitado", "Pendente", "Aprovado", "Cancelado", "Vencido"] as const;
export type OrcamentoStatus = typeof OrcamentoStatusOptions[number];

export const CancellationReasonOptions = ["Valor alto", "Sem orçamento no momento", "Vou deixar para outro momento", "Outros"] as const;
export type CancellationReason = typeof CancellationReasonOptions[number];

export const PaymentMethodOptions = ["Cartão de crédito", "Cartão de débito", "Pix", "Dinheiro"] as const;
export type PaymentMethod = typeof PaymentMethodOptions[number];

export interface OrcamentoDetalhes {
  id: string;
  orcamento_id: string;
  created_at: string;
  status_final: OrcamentoStatus;
  motivo_rejeicao?: CancellationReason;
  motivo_rejeicao_texto?: string;
  metodo_pagamento?: PaymentMethod;
}

export interface Orcamento {
  id: string;
  numero_orcamento: string;
  created_at: string;
  nome_cliente: string;
  /** Cliente cadastrado; sem ele, é consumidor final (vale nome_cliente). */
  cliente_id?: string | null;
  contato_cliente?: string;
  /** Dia da entrega que o vendedor combina ao orçar ("AAAA-MM-DD"); o cliente vê. Vira a do pedido ao aprovar. Ver lib/pedidos/entrega. */
  data_entrega?: string | null;
  data_orcamento?: string; // ISO string date
  valor_total_orcamento?: number;
  status_orcamento?: OrcamentoStatus;
  share_link?: string | null;
  itens: OrcamentoItem[];
  detalhes?: OrcamentoDetalhes | null;
  desconto_global_percentual?: number;
  despesas_adicionais?: number;
  ajuste_valor?: number;
  pedido?: {
    id: string;
    numero_pedido: string;
    status_pedido: PedidoStatus;
    motivo_cancelamento?: string | null;
    motivo_cancelamento_texto?: string | null;
  } | null;
}

export interface OrcamentoItem {
  /** Kit: o que vem dentro de uma unidade. */
  componentes?: { nome: string; quantidade: number }[];
  id: string;
  orcamento_id: string;
  produto_id: string;
  quantidade: number;
  preco_unitario_no_momento: number;
  subtotal_item: number;
  produtoNome?: string;
  desconto_percentual?: number;
  imageUrls?: string[];
  pesoTotalGramas?: number;
  tempoImpressaoHoras?: number;
  impressoraNome?: string;
  filamentos?: {
    filamento_id: string;
    peso_gramas: number;
    filamentoNome: string;
  }[];
  /** Material sugerido no orçamento (de qual filamento do produto saiu qual, em quantas unidades). Não baixa estoque. */
  materiais?: { filamentoOriginalId: string; filamentoId: string; unidades: number }[];
  /** Observação interna do item ("2 verde e 2 amarelo"): acompanha a peça no pedido e na produção. Não sai no PDF. */
  observacao?: string | null;
}

export const PedidoStatusOptions = ["A Fazer", "Fazendo", "Feito", "Entregue", "Faturado", "Finalizado", "Cancelado"] as const;
export type PedidoStatus = (typeof PedidoStatusOptions)[number];

/** Por que um pedido "A Fazer" foi cancelado. "Outros" pede o detalhe. */
export const MotivoCancelamentoPedidoOptions = ["Cliente desistiu", "Prazo não atende", "Erro no pedido", "Pedido duplicado", "Outros"] as const;
export type MotivoCancelamentoPedido = (typeof MotivoCancelamentoPedidoOptions)[number];

interface PedidoItemFilamento {
  filamento_id: string;
  filamentoNome: string;
  peso_gramas: number;
}

interface PedidoItem {
  id: string;
  quantidade: number;
  preco_unitario_no_momento: number;
  subtotal_item: number;
  desconto_percentual?: number;
  produtoId?: string;
  produtoNome?: string;
  tempoImpressaoHoras?: number;
  impressoraNome?: string;
  /** Por unidade do item. No kit, a soma dos itens dele. */
  filamentos?: PedidoItemFilamento[];
  /** Material sugerido no orçamento. A baixa confirma ou troca, em "Feito". */
  materiais?: { filamentoOriginalId: string; filamentoId: string; unidades: number }[];
  /** Kit: o que vai dentro de uma unidade (para a lista de produção). */
  componentes?: { nome: string; quantidade: number }[];
  /** Observação interna do item, a do orçamento. */
  observacao?: string | null;
}

export interface Pedido {
  id: string;
  numero_pedido: string;
  created_at: string;
  orcamento_id: string;
  user_id: string;
  nome_cliente: string;
  /** Do orçamento: cliente cadastrado, ou null (consumidor final). */
  cliente_id?: string | null;
  /** Do orçamento ("Observações / Contato"): o que quem imprime precisa ler. */
  observacoes?: string | null;
  /** Dia da entrega combinado com o cliente ("AAAA-MM-DD", calendário do estúdio); null = sem data. Ver lib/pedidos/entrega. */
  data_entrega?: string | null;
  valor_total: number;
  valor_total_final?: number | null;
  status_pedido: PedidoStatus;
  metodo_pagamento?: string;
  estoque_deduzido: boolean;
  /** 'ESTOQUE_ACABADO' quando as peças saem do estoque de prontos. */
  origem_venda?: string | null;
  data_faturamento?: string;
  itens: PedidoItem[];
  dizimo?: boolean;
  motivo_cancelamento?: string | null;
  motivo_cancelamento_texto?: string | null;
  cancelado_em?: string | null;
  /** Itens (ou parte das unidades) cancelados enquanto o pedido ainda produzia, do mais recente ao mais antigo. */
  cancelamentos?: import('@/lib/pedidos/cancelamentoDeItem').CancelamentoGravado[];
}

export const FinalidadeOptions = [
  "Uso Pessoal",
  "Ferramenta/Acessório",
  "Presente",
  "Teste de Material/Impressora",
  "Produção para Estoque",
  "Encomenda",
  "Peça de Reposição",
  "Brinde",
  "Outro"
] as const;
export type FinalidadeImpressao = typeof FinalidadeOptions[number];

export interface ConsumoReal {
  id: string;
  tipo_filamento_id: string;
  quantidade_consumida_g: number;
}

export interface ImpressaoAvulsa {
  id: string;
  created_at: string;
  user_id: string;
  produto_id: string;
  destinatario: string;
  finalidade: FinalidadeImpressao;
  status: 'A Fazer' | 'Concluída';
  estoque_deduzido: boolean;
  quantidade: number;
  consumo_real: ConsumoReal[];

  /**
   * O que a farm executou, quando a produção veio de lá.
   *
   * `trabalhos = 0` é o registro feito à mão — impressão em máquina não
   * conectada, pendrive, retrabalho. Distinguir importa: só o registro
   * manual pede que alguém declare o filamento, porque no outro a máquina
   * já mediu.
   */
  farm?: {
    trabalhos: number;
    concluidos: number;
    falhos: number;
    imprimindo: number;
    duracaoRealS: number | null;
    pesoRealG: number | null;
    maquinas: string | null;
    iniciadoEm: string | null;
    finalizadoEm: string | null;
  };
  consumo_origem?: 'manual' | 'farm' | 'sem_baixa';
}

export interface Empresa {
  id: string;
  user_id: string;
  created_at: string;
  razao_social: string;
  fantasia?: string;
  email?: string;
  telefone?: string;
  whatsapp?: string;
  estado?: string;
  municipio?: string;
  logomarca?: string | null;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  youtube?: string;
  // Dados fiscais (do emitente da nota), só dígitos onde é número.
  cnpj?: string | null;
  inscricao_estadual?: string | null;
  inscricao_municipal?: string | null;
  regime_tributario?: 'mei' | 'simples_nacional' | 'regime_normal';
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  ncm_padrao?: string | null;
}

export type ChartPeriod = '30d' | '8w' | '12m' | '3y';

export type { UserRole, Permission, RolesConfig, RoleConfig };

// Tipos para Relatórios
export interface FilamentConsumptionData {
  filamentId: string;
  displayName: string;
  totalGramsConsumed: number;
  currentStockGrams: number;
}

export interface ProductPerformanceData {
  productId: string;
  productName: string;
  imageUrl: string | null;
  totalQuantity: number;
  totalRevenue: number;
  /** Custo estimado: cadastro do produto; no kit, os itens do dia da venda + extras. */
  totalCost: number;
  tipo: TipoDeProduto;
  /** Visão por peça: quantas destas unidades saíram dentro de kits. */
  quantidadeEmKits: number;
}

export interface FilamentStockEntry {
  id: string;
  data_compra: string;
  nota_fiscal: string | null;
  quantidade_adicionada_gramas: number;
  quantidade_rolos: number;
  custo_total: number;
  custo_individual: number;
  fornecedor_nome: string;
  filamento_nome: string;
}

export interface StockAdjustmentEntry {
  id: string;
  data_compra: string;
  quantidade_adicionada_gramas: number;
  custo_total: number;
  filamento_nome: string;
  observacoes: string | null;
  tipo_ajuste: 'positivo' | 'negativo';
}

export interface PurchaseSuggestion {
  filamentId: string;
  displayName: string;
  currentStockGrams: number;
  avgDailyConsumptionGrams: number;
  daysRemaining: number;
  reason: 'Estoque Baixo' | 'Alta Demanda';
}

export interface Despesa {
  id?: string;
  user_id?: string;
  created_at?: string;
  descricao: string;
  categoria: string;
  valor: number;
  data_despesa: string;
  observacoes?: string | null;
  origem?: 'estoque_filamento' | 'manual' | 'energia_rateio' | 'compra';
}

export interface PurchaseData {
  fornecedorId: string;
  notaFiscal?: string | null;
  dataCompra: Date | string;
  valorTotalNota: number;
  itens: Array<{
    filamentoId: string;
    quantidadeRolos: number;
    pesoRoloGramas: number;
    custoRolo: number;
  }>;
}

export interface CustosFixos {
  valor_kwh: number;
  percentual_falhas: number;
}

export interface EstoqueProdutoAcabado {
  id: string;
  user_id: string;
  produto_id: string;
  quantidade_estoque: number;
  created_at: string;
  updated_at: string;
  produtoNome?: string;
}

export interface MovimentacaoEstoqueProduto {
  id: string;
  user_id: string;
  produto_id: string;
  tipo_movimentacao: string;
  quantidade: number;
  origem?: string | null;
  destino?: string | null;
  observacoes?: string | null;
  usuario_responsavel?: string | null;
  created_at: string;
  produtoNome?: string;
}

export type ClassificacaoParceiro = 'Papelaria' | 'Mercado' | 'Padaria' | 'Conveniência' | 'Loja de Presentes' | 'Loja de Informática' | 'Outros';

export interface ParceiroConsignado {
  id: string;
  user_id: string;
  /**
   * Cliente cadastrado. Com ele, nome, documento, contato e endereço abaixo
   * são cópia do cadastro, mantida pelo banco. Sem ele, valem os digitados.
   */
  cliente_id?: string | null;
  razao_social: string;
  nome_fantasia: string;
  cnpj?: string | null;
  responsavel: string;
  telefone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  endereco?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  percentual_comissao: number;
  percentual_lucro?: number | null;
  forma_pagamento_preferencial?: string | null;
  prazo_prestacao_contas_dias: number;
  classificacao: ClassificacaoParceiro;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface SaldoConsignadoParceiro {
  id: string;
  user_id: string;
  parceiro_id: string;
  produto_id: string;
  quantidade_enviada: number;
  quantidade_vendida: number;
  quantidade_devolvida: number;
  quantidade_danificada: number;
  saldo_atual: number;
  created_at: string;
  updated_at: string;
  produtoNome?: string;
  /** Preço calculado do produto HOJE. Serve para comparar, não para cobrar. */
  precoVendaCalculado?: number;
  /** Preço da etiqueta, herdado da remessa. É por este que o acerto cobra. */
  preco_venda_praticado?: number | null;
}

export interface RemessaConsignado {
  id: string;
  user_id: string;
  parceiro_id: string;
  numero_remessa: string;
  /**
   * 'Enviando' é estado de trânsito: a remessa está sendo processada item a
   * item. Se ficar parada nele, o envio morreu no meio e parte do estoque já
   * saiu -- a auditoria acusa e reenviar está bloqueado.
   */
  status: 'Rascunho' | 'Enviando' | 'Enviado' | 'Cancelado';
  data_envio?: string | null;
  observacoes?: string | null;
  created_at: string;
  updated_at: string;
  parceiroNome?: string;
  itens?: RemessaItem[];
}

export interface RemessaItem {
  id: string;
  remessa_id: string;
  produto_id: string;
  quantidade: number;
  preco_venda_praticado: number;
  produtoNome?: string;
}

export interface PrestacaoContas {
  id: string;
  user_id: string;
  parceiro_id: string;
  data_prestacao: string;
  valor_bruto: number;
  valor_comissao: number;
  valor_liquido: number;
  pago: boolean;
  data_pagamento?: string | null;
  observacoes?: string | null;
  created_at: string;
  updated_at: string;
  parceiroNome?: string;
  itens?: PrestacaoContasItem[];
}

export interface PrestacaoContasItem {
  id: string;
  prestacao_id: string;
  produto_id: string;
  quantidade_vendida: number;
  quantidade_devolvida: number;
  quantidade_danificada: number;
  preco_venda_praticado: number;
  comissao_percentual_aplicada: number;
  produtoNome?: string;
}



/**
 * Um achado da auditoria de integridade dos dados.
 *
 * Forma única para coisas de naturezas diferentes — gramas, reais,
 * unidades — porque a tela lista todas juntas. `numero` é o tamanho do
 * problema, e é ele que ordena por gravidade real em vez de ordem
 * alfabética do texto.
 */
export interface AchadoAuditoria {
  area: 'estoque' | 'custo' | 'producao';
  /** Identificador da conferência, para agrupar e explicar. */
  verificacao: string;
  severidade: 'alta' | 'media' | 'baixa';
  entidadeId: string | null;
  entidade: string;
  problema: string;
  numero: number | null;
  unidade: string;
  /** O que fazer. Achado sem saída vira ruído na próxima vez que alguém abrir. */
  acao: string;
}
